import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { parseMoney } from "@shared/money";

export interface CapitalProfile {
  id?: string;
  companyId?: string;
  capitalTypes: string[] | null;
  minDealSize: string | null;
  maxDealSize: string | null;
  sweetSpot: string | null;
  minEbitda: string | null;
  industryFocus: string[] | null;
  industryExclusions: string[] | null;
  geographicFocus: string | null;
  collateralTypes: string[] | null;
  typicalRate: string | null;
  typicalLeverage: string | null;
  structureNotes: string | null;
  speedToClose: string | null;
  notes: string | null;
}

/** A capital provider's lending profile and its writers. Amounts go through shared/money.ts, the rule the server enforces. */
export function useCapitalProfile(companyId: string | undefined) {
  const queryClient = useQueryClient();
  const { data: profile, isLoading } = useQuery<CapitalProfile | null>({
    queryKey: ["capital-profile", companyId],
    queryFn: async () => {
      const r = await fetch(`/api/companies/${companyId}/capital-profile`, { credentials: "include" });
      if (!r.ok) throw new Error("Failed to fetch");
      return r.json();
    },
    enabled: !!companyId,
  });

  const saveProfile = async (updates: Partial<CapitalProfile>) => {
    const merged = { ...(profile || {}), ...updates };
    const res = await fetch(`/api/companies/${companyId}/capital-profile`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(merged),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      toast.error(body.error ?? "Couldn't save");
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["capital-profile", companyId] });
    toast.success("Saved");
  };

  // A unit slip is caught here with the fix on offer, rather than after a 400.
  const saveMoney = (field: "minDealSize" | "maxDealSize" | "minEbitda") => (v: string) => {
    const p = parseMoney(v, field === "minEbitda" ? "lenderEbitda" : "dealSize");
    if (p.status === "empty") return saveProfile({ [field]: null });
    if (p.status === "ok") return saveProfile({ [field]: String(p.dollars) });
    const fix = p.suggestion;
    toast.error(
      p.message ?? "Couldn't read that amount",
      fix ? { action: { label: `Use ${fix.display}`, onClick: () => saveProfile({ [field]: String(fix.dollars) }) } } : undefined,
    );
  };

  return { profile, isLoading, saveProfile, saveMoney };
}

export type CapitalProfileState = ReturnType<typeof useCapitalProfile>;
